import { zipSync } from "fflate";
import type { JobSnapshot } from "~/types/media-subtitle";
import { getConvertedPreview, getCurrentPreviewSourceFormat } from "~/utils/media-subtitle-workflow";
import type { SubtitleFormat } from "~/utils/subtitle-format";
import { sanitizeFilename } from "~/utils/tools";

/**
 * Fetches a finished job's subtitle in `fmt`. Returns null when the job has no
 * downloadable content (e.g. no speech, or still running).
 */
export async function fetchJobSubtitle(jobId: string, fmt: SubtitleFormat, signal?: AbortSignal) {
  const res = await $fetch<{ job: JobSnapshot }>("/api/video-to-subtitle/platform/status", {
    params: { job: jobId },
    signal,
  });
  const content = res.job.activePreview || res.job.translatedPreview || res.job.transcriptPreview;
  if (!content) {
    return null;
  }
  return getConvertedPreview(content, getCurrentPreviewSourceFormat(res.job.format), fmt);
}

/** Triggers a browser download of `blob` as `fileName`. */
export function saveBlob(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    if (document.body.contains(anchor)) {
      document.body.removeChild(anchor);
    }
    URL.revokeObjectURL(objectUrl);
  }, 150);
}

/**
 * Zips text files (names made unique, "name (1)") and downloads the archive.
 * Returns false when there was nothing to zip.
 */
export function saveTextFilesAsZip(
  entries: Array<{ name: string; content: string }>,
  extension: string,
  zipName = `subtitles_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.zip`
) {
  const fileNames = new Set<string>();
  const files: Record<string, Uint8Array> = {};

  for (const entry of entries) {
    const baseName = sanitizeFilename(entry.name) || "subtitle";
    let uniqueName = baseName;
    let counter = 1;
    while (fileNames.has(uniqueName)) {
      uniqueName = `${baseName} (${counter++})`;
    }
    fileNames.add(uniqueName);
    files[`${uniqueName}.${extension}`] = new TextEncoder().encode(entry.content);
  }

  if (Object.keys(files).length === 0) {
    return false;
  }

  const zipContent = zipSync(files);
  saveBlob(new Blob([new Uint8Array(zipContent)], { type: "application/zip" }), zipName);
  return true;
}
